import React, { useState, useEffect } from 'react';
import { Phone, Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, switchLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const homeUrl = language === 'en' ? '/en' : '/';

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-effect shadow-md py-3' : 'bg-transparent py-5'
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a href={homeUrl} className="flex-shrink-0" data-testid="logo-link">
            <img 
              src="https://i0.wp.com/bianhi.com/wp-content/uploads/2025/02/logo.png?fit=1698%2C721&ssl=1" 
              alt="Bianhi Medical Hair Restoration" 
              className="h-12 md:h-14 w-auto"
              loading="eager"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            <button 
              onClick={() => scrollToSection('servicios')}
              className="text-slate-700 hover:text-teal-primary font-medium transition-colors"
            >
              {t('nav.services')}
            </button>
            <button 
              onClick={() => scrollToSection('resultados')}
              className="text-slate-700 hover:text-teal-primary font-medium transition-colors"
            >
              {t('nav.results')}
            </button>
            <button 
              onClick={() => scrollToSection('proceso')}
              className="text-slate-700 hover:text-teal-primary font-medium transition-colors"
            >
              {t('nav.process')}
            </button>
            <button 
              onClick={() => scrollToSection('contacto')}
              className="text-slate-700 hover:text-teal-primary font-medium transition-colors"
            >
              {t('nav.contact')}
            </button>
          </nav>

          {/* CTA Buttons + Language Switcher */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language Switcher */}
            <div className="flex items-center gap-1 border border-slate-200 rounded-full p-1" data-testid="language-switcher">
              <button
                onClick={() => switchLanguage('es')}
                className={`w-8 h-8 rounded-full overflow-hidden border-2 transition-all ${
                  language === 'es' ? 'border-[#009d9f] scale-110' : 'border-transparent opacity-60 hover:opacity-100'
                }`}
                title="Español"
              >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/f/fc/Flag_of_Mexico.svg" 
                  alt="ES" 
                  className="w-full h-full object-cover"
                />
              </button>
              <button
                onClick={() => switchLanguage('en')}
                className={`w-8 h-8 rounded-full overflow-hidden border-2 transition-all ${
                  language === 'en' ? 'border-[#009d9f] scale-110' : 'border-transparent opacity-60 hover:opacity-100'
                }`}
                title="English"
              >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/a/a4/Flag_of_the_United_States.svg" 
                  alt="EN" 
                  className="w-full h-full object-cover"
                />
              </button>
            </div>

            <a 
              href="tel:+526645142275"
              className="flex items-center gap-2 text-slate-700 hover:text-teal-primary transition-colors"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span className="font-medium">664 514 2275</span>
            </a>
            <Button 
              onClick={() => scrollToSection('contacto')}
              className="bg-[#009d9f] hover:bg-[#008486] text-white rounded-full px-6 py-2 font-semibold transition-all hover:-translate-y-0.5 hover:shadow-lg"
              data-testid="cta-button"
            >
              {t('nav.schedule')}
            </Button>
          </div>

          {/* Mobile Phone + Language + Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            {/* Mobile Language Switcher */}
            <div className="flex items-center gap-1" data-testid="mobile-language-switcher">
              <button
                onClick={() => switchLanguage('es')}
                className={`w-7 h-7 rounded-full overflow-hidden border-2 transition-all ${
                  language === 'es' ? 'border-[#009d9f]' : 'border-transparent opacity-60'
                }`}
              >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/f/fc/Flag_of_Mexico.svg" 
                  alt="ES" 
                  className="w-full h-full object-cover"
                />
              </button>
              <button
                onClick={() => switchLanguage('en')}
                className={`w-7 h-7 rounded-full overflow-hidden border-2 transition-all ${
                  language === 'en' ? 'border-[#009d9f]' : 'border-transparent opacity-60'
                }`}
              >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/a/a4/Flag_of_the_United_States.svg" 
                  alt="EN" 
                  className="w-full h-full object-cover"
                />
              </button>
            </div>

            <a 
              href="tel:+526645142275"
              className="flex items-center gap-1.5 bg-[#009d9f] text-white rounded-full px-3 py-2 text-sm font-medium"
              data-testid="mobile-phone-link"
            >
              <Phone className="w-4 h-4" />
              <span>{t('nav.call')}</span>
            </a>
            <button 
              className="p-2 text-slate-700"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-slate-100 pt-4" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              <button 
                onClick={() => scrollToSection('servicios')}
                className="text-slate-700 hover:text-teal-primary font-medium text-left"
              >
                {t('nav.services')}
              </button>
              <button 
                onClick={() => scrollToSection('resultados')}
                className="text-slate-700 hover:text-teal-primary font-medium text-left"
              >
                {t('nav.results')}
              </button>
              <button 
                onClick={() => scrollToSection('proceso')}
                className="text-slate-700 hover:text-teal-primary font-medium text-left"
              >
                {t('nav.process')}
              </button>
              <button 
                onClick={() => scrollToSection('contacto')}
                className="text-slate-700 hover:text-teal-primary font-medium text-left"
              >
                {t('nav.contact')}
              </button>
              <a 
                href="tel:+526645142275"
                className="flex items-center gap-2 text-teal-primary font-medium"
              >
                <Phone className="w-4 h-4" />
                <span>664 514 2275</span>
              </a>
              <Button 
                onClick={() => scrollToSection('contacto')}
                className="bg-[#009d9f] hover:bg-[#008486] text-white rounded-full px-6 py-2 font-semibold w-full"
              >
                {t('nav.schedule')}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
